import { rfidLocation } from './rfidLocation';
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import api from '../axiosConfig';
import { v4 as uuidv4 } from 'uuid';
import './RfidQuickLogin.css';

// These keyboard readers emit the ten-digit card number, including leading zeroes.
export default function RfidQuickLogin() {
  const busy = useRef(false);
  const [status, setStatus] = useState(() => sessionStorage.getItem('rfid-attendance-message') || '');
  useEffect(() => { sessionStorage.removeItem('rfid-attendance-message'); }, []);
  const [working, setWorking] = useState(false);
  useEffect(() => {
    if (!status || working) return;
    const dismissTimer = setTimeout(() => setStatus(''), 4000);
    return () => clearTimeout(dismissTimer);
  }, [status, working]);
  useEffect(() => {
    let buffer = '', last = 0, timer, origin, originalValue;
    const finish = async () => {
      clearTimeout(timer);
      const card = buffer; buffer = '';
      if (!/^\d{10}$/.test(card) || busy.current) return;
      if (origin && 'value' in origin) {
        Object.getOwnPropertyDescriptor(Object.getPrototypeOf(origin), 'value')?.set?.call(origin, originalValue);
        origin.dispatchEvent(new Event('input', { bubbles: true }));
        origin.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const guard = new Event('wip-before-worker-switch', { cancelable: true });
      if (!window.dispatchEvent(guard)) { setStatus('Please wait for the current scan or save to finish, then tap your card again.'); return; }
      busy.current = true; setWorking(true); setStatus('Reading card…');
      try {
        let deviceToken = localStorage.getItem('deviceToken');
        if (!deviceToken) { deviceToken = uuidv4(); localStorage.setItem('deviceToken', deviceToken); }
        // A rejected replacement card must not log out the current worker via the API interceptor.
        const location = await rfidLocation();
        const response = await axios.post(`${api.defaults.baseURL}/api/rfid-login`, {
          RFID: card, deviceToken, deviceName: navigator.platform, browser: navigator.userAgent,
          dashboardType: 'WIP', loginLocation: 'RFID terminal', ...location,
        }, { withCredentials: true, timeout: 20000 });
        const worker = response.data.user;
        const attendanceMessage = response.data.attendance?.status === 'pending' ? response.data.attendance.message : '';
        let currentWorker = null;
        try { currentWorker = JSON.parse(localStorage.getItem('user') || 'null'); } catch { /* No valid local session. */ }
        if (window.location.pathname.toLowerCase() === '/user-dashboard'
          && currentWorker?.EmployeeID != null
          && String(currentWorker.EmployeeID) === String(worker.EmployeeID)) {
          // Same worker: keep the mounted scanner, selected supervisor and drafts intact.
          busy.current = false; setWorking(false);
          setStatus(attendanceMessage ? `Aap already logged in hain. ${attendanceMessage}` : 'Aap already logged in hain.');
          window.dispatchEvent(new Event('wip-worker-switch-failed'));
          return;
        }
        // A different worker starts with an empty scan session.
        for (const key of Object.keys(sessionStorage)) {
          if (key.startsWith('worker-bundle-draft-v1:')) sessionStorage.removeItem(key);
        }
        localStorage.setItem('user', JSON.stringify(worker));
        localStorage.setItem('userData', JSON.stringify(worker));
        localStorage.setItem('loginTime', new Date().toISOString());
        localStorage.setItem('sessionExpiration', new Date(Date.now() + 12 * 3600000).toISOString());
        // Remount the dashboard so the previous worker's data and in-flight views cannot carry over.
        if (attendanceMessage) sessionStorage.setItem('rfid-attendance-message', attendanceMessage);
        window.location.replace('/User-dashboard');
      } catch (error) {
        setStatus(error.response?.data?.error || 'Card login failed. Please tap again.');
        busy.current = false; setWorking(false);
        window.dispatchEvent(new Event('wip-worker-switch-failed'));
      }
    };
    const onKey = event => {
      if (typeof event.key !== 'string' || !event.key) {
        clearTimeout(timer); buffer = ''; return;
      }
      if (event.ctrlKey || event.altKey || event.metaKey || event.isComposing) { buffer = ''; return; }
      if (event.key === 'Shift') return;
      clearTimeout(timer);
      if (Date.now() - last > 100) buffer = '';
      last = Date.now();
      if (event.key === 'Enter' || event.key === 'Tab') {
        if (/^\d{10}$/.test(buffer)) { event.preventDefault(); event.stopImmediatePropagation(); finish(); }
        else buffer = '';
      } else if (event.key.length === 1) {
        if (!buffer) { origin = event.target; originalValue = origin?.value; }
        buffer += event.key;
        if (buffer.length > 64) buffer = '';
        if (/^\d{10}$/.test(buffer)) {
          event.preventDefault(); event.stopImmediatePropagation();
          timer = setTimeout(finish, 150);
        }
      } else buffer = '';
    };
    window.addEventListener('keydown', onKey, true);
    return () => { clearTimeout(timer); window.removeEventListener('keydown', onKey, true); };
  }, []);
  if (!status) return null;
  const alreadyLoggedIn = status === 'Aap already logged in hain.';
  return <div role={working || alreadyLoggedIn ? 'status' : 'alert'} className={working ? 'rfid-login-progress' : `rfid-login-error${alreadyLoggedIn ? ' rfid-login-success' : ''}`}>
    {!working && <span className="rfid-login-error-icon" aria-hidden="true">{alreadyLoggedIn ? '✓' : '!'}</span>}
    <strong>{status}</strong>{!working && <button type="button" aria-label="Close notification" onClick={() => setStatus('')}>×</button>}
  </div>;
}
